import { View, Text, Platform } from "react-native";
import React, { useEffect, useRef, useState } from "react";
import ScrollContainer from "components/general/ScrollContainer";
import Seo from "components/general/Seo";
import Container from "components/general/Container";
import { Box, Typography, Button, HStack, VStack, Image, Pressable } from "components/ui";
import PageHeader from "components/general/Headers/PageHeader";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import FormField from "components/form/FormField";
import { AlertCircle, ArrowLeft, ArrowRight, Check, CheckCircle, Plus, Send, SendHorizonal } from "lucide-react-native";
import Section from "components/general/Section";
import { useSiteRegion } from "utils/hooks/useSiteSection";
import SignatureTabs from "components/general/SignatureTabs";
import SignatureCanvas from "react-signature-canvas";
import TermsAndConditions from "components/account-application/TermsAndConditions";
import { generatePDF } from "components/account-application/GeneratePDF";
import DismissableContent from "components/general/DismissableContent";

export interface FormValues {
	// Applicant Details
	applicantFullName: string;
	mobilePhone: string;
	applicantEmail: string;
	requiresPurchaseOrder: boolean;
	invoiceEmail: string;

	// The Application
	abcoCustomerNumber: string;
	entityType: string;
	abn: string;
	acn: string;
	allOrdersRequirePO: boolean;

	// Company Details
	legalEntityName: string;
	tradingName: string;

	// Physical Address
	physicalStreetAddress: string;
	physicalSuburb: string;
	physicalState: string;
	physicalPostcode: string;

	// Postal Address
	postalSameAsPhysical: boolean;
	postalStreetAddress: string;
	postalSuburb: string;
	postalState: string;
	postalPostcode: string;

	// Contact Person
	ownerContactName: string;
	ownerMobilePhone: string;
	ownerEmail: string;

	// Trading Details
	natureOfBusiness: string;
	estimatedMonthlySpend: string;

	// Trade Reference
	referenceContactName: string;
	referenceCompanyName: string;
	referenceEmail: string;
	referenceMobilePhone: string;

	// Abco Contact
	abcoContactName: string;

	// Company Contacts
	companyContacts: Array<{
		fullName: string;
		position: string;
		mobilePhone: string;
		email: string;
		accountsContactName: string;
		accountsContactNumber: string;
	}>;
}

const validationSchema = Yup.object().shape({
	// Applicant Details
	applicantFullName: Yup.string().required("Full name is required"),
	mobilePhone: Yup.string()
		.matches(/^[0-9\s]+$/, "Mobile number must contain only numbers and spaces")
		.required("Mobile number is required"),
	applicantEmail: Yup.string().email("Invalid email").required("Email is required"),
	invoiceEmail: Yup.string().email("Invalid email").required("Invoice email is required"),

	// The Application
	abcoCustomerNumber: Yup.string().required("Abco customer number is required"),
	entityType: Yup.string().required("Entity type is required"),
	abn: Yup.string().required("ABN is required"),
	acn: Yup.string().when("entityType", {
		is: "company",
		then: (schema) => schema.required("ACN is required"),
	}),

	// Company Details
	legalEntityName: Yup.string().required("Legal entity name is required"),
	tradingName: Yup.string().required("Trading name is required"),

	// Physical Address
	physicalStreetAddress: Yup.string().required("Street address is required"),
	physicalSuburb: Yup.string().required("Suburb is required"),
	physicalState: Yup.string().required("State is required"),
	physicalPostcode: Yup.string().required("Postcode is required"),

	// Postal Address
	postalStreetAddress: Yup.string().when("postalSameAsPhysical", {
		is: (val: boolean) => !val,
		then: (schema) => schema.required("Postal street address is required"),
	}),
	postalSuburb: Yup.string().when("postalSameAsPhysical", {
		is: (val: boolean) => !val,
		then: (schema) => schema.required("Postal suburb is required"),
	}),
	postalState: Yup.string().when("postalSameAsPhysical", {
		is: (val: boolean) => !val,
		then: (schema) => schema.required("Postal state is required"),
	}),
	postalPostcode: Yup.string().when("postalSameAsPhysical", {
		is: (val: boolean) => !val,
		then: (schema) => schema.required("Postal postcode is required"),
	}),

	// Contact Person
	ownerContactName: Yup.string().required("Owner contact name is required"),
	ownerMobilePhone: Yup.string()
		.matches(/^[0-9\s]+$/, "Mobile number must contain only numbers and spaces")
		.required("Owner mobile phone is required"),
	ownerEmail: Yup.string().email("Invalid email").required("Owner email is required"),

	// Trading Details
	natureOfBusiness: Yup.string().required("Nature of business is required"),
	estimatedMonthlySpend: Yup.string().required("Estimated monthly spend is required"),

	// Abco Contact
	abcoContactName: Yup.string(),

	// Company Contacts
	companyContacts: Yup.array().of(
		Yup.object().shape({
			fullName: Yup.string().required("Full name is required"),
			position: Yup.string().required("Position is required"),
			mobilePhone: Yup.string().required("Mobile phone is required"),
			email: Yup.string().email("Invalid email").required("Email is required"),
			accountsContactName: Yup.string().required("Accounts contact name is required"),
			accountsContactNumber: Yup.string().required("Accounts contact number is required"),
		}),
	),
});

const initialValues: FormValues = {
	applicantFullName: "",
	mobilePhone: "",
	applicantEmail: "",
	requiresPurchaseOrder: false,
	invoiceEmail: "",
	abcoCustomerNumber: "",
	entityType: "",
	abn: "",
	acn: "",
	allOrdersRequirePO: false,
	legalEntityName: "",
	tradingName: "",
	physicalStreetAddress: "",
	physicalSuburb: "",
	physicalState: "",
	physicalPostcode: "",
	postalSameAsPhysical: false,
	postalStreetAddress: "",
	postalSuburb: "",
	postalState: "",
	postalPostcode: "",
	ownerContactName: "",
	ownerMobilePhone: "",
	ownerEmail: "",
	natureOfBusiness: "",
	estimatedMonthlySpend: "",
	referenceContactName: "",
	referenceCompanyName: "",
	referenceEmail: "",
	referenceMobilePhone: "",
	abcoContactName: "",
	companyContacts: [],
};

const stateOptions = [
	{ value: "nsw", label: "New South Wales" },
	{ value: "vic", label: "Victoria" },
	{ value: "qld", label: "Queensland" },
	{ value: "sa", label: "South Australia" },
	{ value: "wa", label: "Western Australia" },
	{ value: "tas", label: "Tasmania" },
	{ value: "nt", label: "Northern Territory" },
	{ value: "act", label: "Australian Capital Territory" },
];

const entityTypeOptions = [
	{ value: "sole-trader", label: "Sole Trader" },
	{ value: "company", label: "Company" },
	{ value: "trust", label: "Trust" },
];

const AccountApplication = () => {
	const [currentStep, setCurrentStep] = useState(1);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const sigCanvasRef = useRef<SignatureCanvas | null>(null);
	// const [htmlForm, setHtmlForm] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [checkedStatus, setCheckedStatus]: any = useState({});
	const [canvasEmpty, setCanvasEmpty] = useState(true);
	const [buttonDisabled, setButtonDisabled] = useState(true);
	const [isDrawingMode, setIsDrawingMode] = useState(true);
	const [typedSignature, setTypedSignature] = useState("");
	const [shownButtonsIndices, setShownButtonsIndices] = useState<Set<number>>(new Set());
	const [addClicked, setAddClicked] = useState(false);
	// const [htmlForm, setHtmlForm] = useState("");
	const [selectedFont, setSelectedFont] = useState({
		value: "Pacifico",
		label: "Pacifico",
	});

	if (Platform.OS !== "web") {
		return null;
	}
	const handleAccept = () => {
		setAcceptedTerms(!acceptedTerms);
	};
	useEffect(() => {
		const signatureProvided = !canvasEmpty || typedSignature !== "";
		setButtonDisabled(!(acceptedTerms && signatureProvided));
	}, [canvasEmpty, acceptedTerms, typedSignature]);

	const handleCanvasChange = () => {
		if (sigCanvasRef.current) {
			setCanvasEmpty(sigCanvasRef.current.isEmpty());
		}
	};

	const clearSignature = () => {
		if (sigCanvasRef.current) {
			sigCanvasRef.current.clear();
			setCanvasEmpty(true);
		}
		setTypedSignature("");
	};

	function formatData(data: any, formSchema: any) {
		let formattedData: any = [];

		formSchema.formSet.forEach((formFieldSet: any) => {
			let formSetData: any = {};
			formFieldSet.formFieldList.forEach((formField: any) => {
				if (formField._type === "formField") {
					const fieldName = formField.fieldName;
					const fieldValue = fieldName.includes(".") ? fieldName.split(".").reduce((obj: any, key: string) => obj && obj[key], data) : data[fieldName];

					if (fieldValue !== undefined && fieldValue !== "") {
						formSetData[fieldName] = {
							label: formField.fieldLabel,
							value: fieldValue,
						};
					}

					if (formField.conditionalFields) {
						formField.conditionalFields.forEach((conditionalField: any) => {
							const conditionalFieldName = conditionalField.fieldName;
							const conditionalFieldValue = conditionalFieldName.includes(".") ? conditionalFieldName.split(".").reduce((obj: any, key: string) => obj && obj[key], data) : data[conditionalFieldName];

							if (conditionalFieldValue !== undefined && conditionalFieldValue !== "") {
								formSetData[conditionalFieldName] = {
									label: conditionalField.fieldLabel,
									value: conditionalFieldValue,
								};
							}
						});
					}
				}
			});

			if (Object.keys(formSetData).length > 0) {
				formattedData.push({ [formFieldSet.title]: formSetData });
			}
		});

		return formattedData;
	}

	const totalSteps = 4;
	const region = useSiteRegion();
	const handleNext = async (formik: any) => {
		const currentStepFields =
			currentStep === 1
				? ["applicantFullName", "mobilePhone", "applicantEmail", "requiresPurchaseOrder", "invoiceEmail", "abcoCustomerNumber", "entityType", "abn", "acn", "legalEntityName", "tradingName", "allOrdersRequirePO"]
				: currentStep === 2
					? ["physicalStreetAddress", "physicalSuburb", "physicalState", "physicalPostcode", "postalSameAsPhysical", "postalStreetAddress", "postalSuburb", "postalState", "postalPostcode"]
					: currentStep === 3
						? ["ownerContactName", "ownerMobilePhone", "ownerEmail", "natureOfBusiness", "estimatedMonthlySpend", "referenceContactName", "referenceCompanyName", "referenceEmail", "referenceMobilePhone", "abcoContactName"]
						: [];

		const touched = currentStepFields.reduce(
			(acc, field) => ({
				...acc,
				[field]: true,
			}),
			{},
		);
		formik.setTouched(touched);

		const errors = await formik.validateForm();
		const hasStepErrors = currentStepFields.some((field) => errors[field]);

		if (!hasStepErrors) {
			setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
		}
	};

	const handleBack = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const handleSubmit = async (values: FormValues) => {
		setIsSubmitting(true);
		try {
			// Format the form data using the existing formatData function
			const formSchema = {
				formSet: [
					{
						title: "Applicant Details",
						formFieldList: [
							{ _type: "formField", fieldName: "applicantFullName", fieldLabel: "Applicant Full Name" },
							{ _type: "formField", fieldName: "mobilePhone", fieldLabel: "Mobile Phone #" },
							{ _type: "formField", fieldName: "applicantEmail", fieldLabel: "Applicant Email" },
							{ _type: "formField", fieldName: "requiresPurchaseOrder", fieldLabel: "Requires Purchase Order" },
							{ _type: "formField", fieldName: "invoiceEmail", fieldLabel: "Invoice Email" },
						],
					},
					{
						title: "The Application",
						formFieldList: [
							{ _type: "formField", fieldName: "abcoCustomerNumber", fieldLabel: "Abco Customer Number" },
							{ _type: "formField", fieldName: "entityType", fieldLabel: "Entity Type" },
							{ _type: "formField", fieldName: "abn", fieldLabel: "ABN" },
							{ _type: "formField", fieldName: "acn", fieldLabel: "ACN" },
							{ _type: "formField", fieldName: "legalEntityName", fieldLabel: "Legal Entity Name" },
							{ _type: "formField", fieldName: "tradingName", fieldLabel: "Trading Name" },
							{ _type: "formField", fieldName: "allOrdersRequirePO", fieldLabel: "All Orders Require PO" },
						],
					},
					{
						title: "Physical Address",
						formFieldList: [
							{ _type: "formField", fieldName: "physicalStreetAddress", fieldLabel: "Street Address" },
							{ _type: "formField", fieldName: "physicalSuburb", fieldLabel: "Suburb" },
							{ _type: "formField", fieldName: "physicalState", fieldLabel: "State" },
							{ _type: "formField", fieldName: "physicalPostcode", fieldLabel: "Postcode" },
						],
					},
					{
						title: "Postal Address",
						formFieldList: [
							{ _type: "formField", fieldName: "postalSameAsPhysical", fieldLabel: "Postal Same as Physical" },
							{ _type: "formField", fieldName: "postalStreetAddress", fieldLabel: "Postal Street Address" },
							{ _type: "formField", fieldName: "postalSuburb", fieldLabel: "Postal Suburb" },
							{ _type: "formField", fieldName: "postalState", fieldLabel: "Postal State" },
							{ _type: "formField", fieldName: "postalPostcode", fieldLabel: "Postal Postcode" },
						],
					},
					{
						title: "Contact Person",
						formFieldList: [
							{ _type: "formField", fieldName: "ownerContactName", fieldLabel: "Owner Contact Name" },
							{ _type: "formField", fieldName: "ownerMobilePhone", fieldLabel: "Owner Mobile Phone" },
							{ _type: "formField", fieldName: "ownerEmail", fieldLabel: "Owner Email" },
						],
					},
					{
						title: "Trading Details",
						formFieldList: [
							{ _type: "formField", fieldName: "natureOfBusiness", fieldLabel: "Nature of Business" },
							{ _type: "formField", fieldName: "estimatedMonthlySpend", fieldLabel: "Estimated Monthly Spend" },
						],
					},
					{
						title: "Trade Reference",
						formFieldList: [
							{ _type: "formField", fieldName: "referenceContactName", fieldLabel: "Reference Contact Name" },
							{ _type: "formField", fieldName: "referenceCompanyName", fieldLabel: "Reference Company Name" },
							{ _type: "formField", fieldName: "referenceEmail", fieldLabel: "Reference Email" },
							{ _type: "formField", fieldName: "referenceMobilePhone", fieldLabel: "Reference Mobile Phone" },
						],
					},
					{
						title: "Abco Contact",
						formFieldList: [{ _type: "formField", fieldName: "abcoContactName", fieldLabel: "Abco Contact Name" }],
					},
					...(values.companyContacts || []).map((_, index) => ({
						title: `Company Contact ${index + 1}`,
						formFieldList: [
							{ _type: "formField", fieldName: `companyContacts.${index}.fullName`, fieldLabel: "Full Name" },
							{ _type: "formField", fieldName: `companyContacts.${index}.position`, fieldLabel: "Position" },
							{ _type: "formField", fieldName: `companyContacts.${index}.mobilePhone`, fieldLabel: "Mobile Phone #" },
							{ _type: "formField", fieldName: `companyContacts.${index}.email`, fieldLabel: "Email" },
							{ _type: "formField", fieldName: `companyContacts.${index}.accountsContactName`, fieldLabel: "Accounts Contact Name" },
							{ _type: "formField", fieldName: `companyContacts.${index}.accountsContactNumber`, fieldLabel: "Accounts Contact Number" },
						],
					})),
				],
			};

			const formattedData = formatData(values, formSchema);

			// Generate PDF with formatted data
			await generatePDF(formattedData, typedSignature, selectedFont, sigCanvasRef);

			setIsSubmitted(true);
		} catch (error) {
			console.error("Error submitting form:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<ScrollContainer>
			<Seo title={"30 Day End Of Month Credit Application Form | Abco"} description={"Apply for a 30 Day End Of Month Credit Account with Abco. Fill out our application form to get started."} />
			<Container>
				{/* <PageHeader title="Credit Application Form" /> */}
				<Section gap={12} height={"$full"}>
					<Box width={"$full"} maxWidth={700} mx="auto">
						<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
							{(formik) => (
								<Box justifyContent="space-between" height={"$full"}>
									{isSubmitted ? (
										<VStack py={24} alignItems="center" justifyContent="center" flex={1} gap={16}>
											<CheckCircle size={48} color="green" />
											<Typography.H3>Application Submitted Successfully!</Typography.H3>
											<Typography.ParagraphSmall>Thank you for your application. We'll process it and get back to you soon.</Typography.ParagraphSmall>
										</VStack>
									) : (
										<>
											<Box gap={12} mb={32} justifyContent="center" alignItems="center">
												<Typography.H5 weight="bold">30 Day End Of Month Credit Application Form</Typography.H5>
												<Typography.ParagraphSmall color="$neutral600">
													Note: Account will be C.O.D until application is fully processed and approved. Services are provided by Abco on the Terms and Conditions of Trade as annexed.
												</Typography.ParagraphSmall>
											</Box>
											<Box position="relative" zIndex={10}>
												{/* Stepper UI */}
												<HStack justifyContent="center" alignItems="center" mb={32}>
													{[1, 2, 3, 4].map((step, idx, arr) => {
														const isCompleted = step < currentStep;
														const isCurrent = step === currentStep;
														const StepCircle = (
															<Box
																width={32}
																height={32}
																borderRadius={16}
																borderWidth={2}
																borderColor={isCurrent ? "$primary" : isCompleted ? "$primary" : "$neutral400"}
																alignItems="center"
																justifyContent="center"
																bg={isCurrent ? "$primary" : isCompleted ? "$basicwhite" : "$basicwhite"}
															>
																{isCompleted ? <Check size={16} color="#1A428A" /> : <Typography.H5 color={isCurrent ? "$basicwhite" : isCompleted ? "$primary" : "$neutral600"}>{step}</Typography.H5>}
															</Box>
														);
														return (
															<React.Fragment key={step}>
																{isCompleted ? <Pressable onPress={() => setCurrentStep(step)}>{StepCircle}</Pressable> : StepCircle}
																{idx < arr.length - 1 && <Box flex={1} height={2} bg="$neutral300" mx={2} />}
															</React.Fragment>
														);
													})}
												</HStack>

												{currentStep === 1 && (
													<VStack gap={16}>
														<Box>
															<Typography.H3 color="$primary">Applicant Details</Typography.H3>
															<Typography.ParagraphSmall color="$neutral600">Please provide your personal information.</Typography.ParagraphSmall>
														</Box>
														<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
															<Typography.H5 color="$basicwhite">Applicant Details</Typography.H5>
														</Box>
														<FormField label="Applicant Full Name*" name="applicantFullName" placeholder="Enter your full name" formik={formik} />
														<FormField label="Mobile Phone #*" name="mobilePhone" placeholder="0400 000 000" formik={formik} />
														<FormField label="Applicant Email*" name="applicantEmail" placeholder="example@address.com" formik={formik} />
														<FormField type="checkbox" name="requiresPurchaseOrder" label="" placeholder="Does your business require a purchase/work order number for payment?" formik={formik} />

														<FormField label="Email address to send invoices/credits/statements to*" name="invoiceEmail" placeholder="example@address.com" formik={formik} />
														<Box mt={24}>
															<Typography.H3 color="$primary">The Application</Typography.H3>
															<Typography.ParagraphSmall color="$neutral600">Please provide your business details.</Typography.ParagraphSmall>
														</Box>
														<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
															<Typography.H5 color="$basicwhite">The Application</Typography.H5>
														</Box>
														<FormField label="Abco Customer Number*" name="abcoCustomerNumber" placeholder="C-XXXXX" formik={formik} />
														<Box position="relative" zIndex={8}>
															<FormField label="Entity Type*" name="entityType" type="select" options={entityTypeOptions} placeholder="--Please Select--" formik={formik} />
														</Box>
														{formik.values.entityType === "company" && <FormField label="ACN*" name="acn" placeholder="Enter your ACN" formik={formik} />}

														<FormField label="ABN*" name="abn" placeholder="Enter your ABN" formik={formik} />

														<FormField label="Legal Entity Name*" name="legalEntityName" placeholder="Enter legal entity name" formik={formik} />
														<FormField label="Trading Name*" name="tradingName" placeholder="Enter trading name" formik={formik} />
														<FormField type="checkbox" name="allOrdersRequirePO" label="" placeholder="All orders require P.O?" formik={formik} />
													</VStack>
												)}

												{currentStep === 2 && (
													<VStack gap={16}>
														<Box>
															<Typography.H3 color="$primary">Contact Details</Typography.H3>
															<Typography.ParagraphSmall color="$neutral600">Please provide your address information.</Typography.ParagraphSmall>
														</Box>
														<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
															<Typography.H5 color="$basicwhite">Physical Address</Typography.H5>
														</Box>
														<FormField label="Street Address*" name="physicalStreetAddress" placeholder="Enter street address" formik={formik} />
														<FormField label="Suburb*" name="physicalSuburb" placeholder="Enter suburb" formik={formik} />
														<Box position="relative" zIndex={8}>
															<FormField label="State*" name="physicalState" type="select" options={stateOptions} placeholder="--Please Select--" formik={formik} />
														</Box>
														<FormField label="Postcode*" name="physicalPostcode" placeholder="Enter postcode" formik={formik} />
														<FormField type="checkbox" name="postalSameAsPhysical" label="" placeholder="Postal Same as Physical Address?" formik={formik} />

														{!formik.values.postalSameAsPhysical && (
															<>
																<FormField label="Postal Address*" name="postalStreetAddress" placeholder="Enter postal address" formik={formik} />
																<FormField label="Suburb*" name="postalSuburb" placeholder="Enter postal suburb" formik={formik} />
																<FormField label="State*" name="postalState" type="select" options={stateOptions} placeholder="--Please Select--" formik={formik} />
																<FormField label="Postcode*" name="postalPostcode" placeholder="Enter postal postcode" formik={formik} />
															</>
														)}
													</VStack>
												)}

												{currentStep === 3 && (
													<VStack gap={16}>
														<Box>
															<Typography.H3 color="$primary">Contact Person & Trading Details</Typography.H3>
															<Typography.ParagraphSmall color="$neutral600">Please provide contact and trading information.</Typography.ParagraphSmall>
														</Box>
														<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
															<Typography.H5 color="$basicwhite">Contact Person</Typography.H5>
														</Box>
														<FormField label="Owner Contact Name*" name="ownerContactName" placeholder="Enter owner contact name" formik={formik} />
														<FormField label="Mobile Phone #*" name="ownerMobilePhone" placeholder="0400 000 000" formik={formik} />
														<FormField label="Email*" name="ownerEmail" placeholder="example@address.com" formik={formik} />
														<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
															<Typography.H5 color="$basicwhite">Trading Details</Typography.H5>
														</Box>
														<FormField label="Nature of Business*" name="natureOfBusiness" placeholder="Enter nature of business" formik={formik} />
														<FormField label="Estimated Monthly $ Spend*" name="estimatedMonthlySpend" placeholder="Enter estimated monthly spend" formik={formik} />

														<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
															<Typography.H5 color="$basicwhite">Trade Reference</Typography.H5>
														</Box>
														<FormField label="Contact Name" name="referenceContactName" placeholder="Enter reference contact name" formik={formik} />
														<FormField label="Company Name" name="referenceCompanyName" placeholder="Enter reference company name" formik={formik} />
														<FormField label="Email" name="referenceEmail" placeholder="Enter reference email" formik={formik} />
														<FormField label="Mobile Phone #" name="referenceMobilePhone" placeholder="Enter reference mobile phone" formik={formik} />

														<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
															<Typography.H5 color="$basicwhite">Abco Contact</Typography.H5>
														</Box>
														<FormField label="Contact Name" name="abcoContactName" placeholder="Enter Abco contact name" formik={formik} />

														<FieldArray name="companyContacts">
															{({ push, remove, form }: any) => (
																<Box>
																	{form.values.companyContacts && form.values.companyContacts.length > 0 && (
																		<VStack gap={16} mb={24}>
																			{form.values.companyContacts.map((contact: any, idx: number) => (
																				<Box gap={12} key={idx}>
																					<Box bg="$primary" p={6} alignItems="center" justifyContent="center">
																						<Typography.H5 color="$basicwhite">Company Contact {idx + 1}</Typography.H5>
																					</Box>
																					<FormField label="Full Name*" name={`companyContacts.${idx}.fullName`} placeholder="Full name" formik={formik} />
																					<FormField label="Position*" name={`companyContacts.${idx}.position`} placeholder="Position" formik={formik} />
																					<FormField label="Mobile Phone #*" name={`companyContacts.${idx}.mobilePhone`} placeholder="Mobile Phone #" formik={formik} />
																					<FormField label="Email*" name={`companyContacts.${idx}.email`} placeholder="Johndoe@email.com" formik={formik} />
																					<FormField label="Accounts Payable Contact Name*" name={`companyContacts.${idx}.accountsContactName`} placeholder="Contact name" formik={formik} />
																					<FormField label="Accounts Payable Contact Number*" name={`companyContacts.${idx}.accountsContactNumber`} placeholder="Contact Number" formik={formik} />
																					{/* <Box style={{ marginTop: 8 }}>
																						<Button variant="secondary" onPress={() => remove(idx)}>
																							<Typography>Remove</Typography>
																						</Button>
																					</Box> */}
																				</Box>
																			))}
																		</VStack>
																	)}
																	<Box alignItems="center" justifyContent="center" style={{ marginBottom: 12 }}>
																		<Button variant="primary" onPress={() => push({ fullName: "", position: "", mobilePhone: "", email: "", accountsContactName: "", accountsContactNumber: "" })}>
																			<Typography.H5 color="$basicwhite">Add Company Contact</Typography.H5>
																			<Plus color="white" />
																		</Button>
																	</Box>
																</Box>
															)}
														</FieldArray>
													</VStack>
												)}

												{currentStep === 4 && (
													<VStack gap={16}>
														<Box>
															<Typography.H3 color="$primary">Finalise Application</Typography.H3>
															<Typography.ParagraphSmall color="$neutral600">Please sign the application form.</Typography.ParagraphSmall>
														</Box>
														<SignatureTabs
															clearSignature={clearSignature}
															sigCanvasRef={sigCanvasRef}
															handleCanvasChange={handleCanvasChange}
															isDrawingMode={isDrawingMode}
															setIsDrawingMode={setIsDrawingMode}
															typedSignature={typedSignature}
															setTypedSignature={setTypedSignature}
															selectedFont={selectedFont}
															setSelectedFont={setSelectedFont}
														/>
														<TermsAndConditions onAccept={handleAccept} acceptedTerms={acceptedTerms} setAcceptedTerms={setAcceptedTerms} />

														{!acceptedTerms && (
															<DismissableContent variant="error" icon={<AlertCircle size={16} color="red" />}>
																<Typography color="$errorbright" fontWeight="bold">
																	Must accept Terms & Conditions.
																</Typography>
															</DismissableContent>
														)}
													</VStack>
												)}
											</Box>

											<HStack position="relative" zIndex={6} justifyContent="space-between" mt={24}>
												{currentStep > 1 && (
													<Button gap={4} variant="secondary" onPress={handleBack}>
														<ArrowLeft />
														<Typography weight="bold">Previous</Typography>
													</Button>
												)}
												{currentStep < totalSteps ? (
													<Button gap={4} ml="auto" variant="secondary" onPress={() => handleNext(formik)}>
														<Typography weight="bold">Next</Typography>
														<ArrowRight />
													</Button>
												) : (
													<Button variant="secondary" onPress={() => formik.handleSubmit()} disabled={isSubmitting || !acceptedTerms}>
														{isSubmitting ? (
															<Typography weight="bold">Submitting...</Typography>
														) : (
															<HStack gap={6} alignItems="center">
																<Typography weight="bold">Send Application</Typography>
																<Send size={16} />
															</HStack>
														)}
													</Button>
												)}
											</HStack>
										</>
									)}
								</Box>
							)}
						</Formik>
					</Box>
				</Section>
			</Container>
		</ScrollContainer>
	);
};

export default AccountApplication;
